export interface OddsPanelProps {
  /** Floor of the cheapest tier — leftmost stat (Figma `543:2745`). */
  minValue: number;
  /** Expected / estimated payout — centre stat, largest type. */
  maxPull: number;
  /** Big-win probability as a percent (0–100). Right stat. */
  bigWinChance: number;
}

function formatDollars(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '$0';
  return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;
}

function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return Number.isInteger(value)
    ? String(value)
    : value.toFixed(2).replace(/\.?0+$/, '');
}

// Centre "Max Pull" value is the visual hero — render it ~60% larger
// than the side values. Same ratio for the centre label vs side labels
// (≈ +15%) keeps the typographic hierarchy when the operator scales
// either size from the Design Constructor.
const CENTER_VALUE_SIZE = 'calc(var(--co-StatsRow-valueSize, 20px) * 1.6)';
const CENTER_LABEL_SIZE = 'calc(var(--co-StatsRow-labelSize, 13px) * 1.15)';

const LABEL_COLOR = 'var(--co-StatsRow-labelColor, rgba(255,255,255,0.5))';
const VALUE_COLOR = 'var(--co-StatsRow-valueColor, #ffffff)';
const FONT = 'var(--co-StatsRow-fontFamily), Inter, system-ui, sans-serif';

/**
 * Modern OddsPanel — pixel-perfect Figma `543:2745`.
 *
 * Design Constructor surface name: `StatsRow`. Knobs follow the same
 * pattern as PackInfoCard — typography (font, value/label colour + size),
 * background (gradient + opacity + glass blur) and border. Default
 * `bgFrom`/`bgTo` mirror the previous hardcoded glass wash so unmodified
 * deployments stay pixel-identical.
 *
 * Anatomy (all coords relative to a 400-wide / 86-tall card):
 *   • Centre column — "Max Pull" label at y=12, big value at y=44.
 *   • Two thin decorative dividers at y=21 flank the "Max Pull" label.
 *   • Left column — "Min Value" label + value at left=20, top=34.
 *   • Right column — "Big Win Chance" label + percent at right=20, top=34,
 *     with a soft green-accent text-shadow glow (themable via --ui-accent).
 */
export function OddsPanel({ minValue, maxPull, bigWinChance }: OddsPanelProps) {
  return (
    // The `stats-row-card` class wraps a @media (min-width: 900px) block in
    // index.css that pulls bg-gradient / border / backdrop-blur from the
    // `--co-StatsRow-*` knobs. On mobile the class is inert, so the section
    // stays transparent and sits directly on the page background — matching
    // the original Figma mobile layout (628:587).
    <section
      aria-label="Pack odds"
      data-co-component="StatsRow"
      className="
        stats-row-card
        relative h-[96px]
        min-[900px]:overflow-hidden
        min-[900px]:[border-radius:var(--co-StatsRow-borderRadius,28px)]
        min-[900px]:shadow-[0_64px_34px_0_rgba(0,0,0,0.05)]
      "
      style={{ fontFamily: FONT }}
    >
      {/* Decorative dividers framing the centre "Max Pull" label. */}
      <div className="pointer-events-none absolute top-[21px] left-5 h-[2px] w-[calc(50%-68px)] rounded-[30px] bg-[#d9d9d9] opacity-20" />
      <div className="pointer-events-none absolute top-[21px] right-5 h-[2px] w-[calc(50%-68px)] rounded-[30px] bg-[#d9d9d9] opacity-20" />

      {/* Centre — "Max Pull" + big value. */}
      <p
        className="
          absolute top-[12px] left-1/2 -translate-x-1/2
          text-center whitespace-nowrap font-semibold tracking-[-0.3px] leading-[22px]
        "
        style={{ color: LABEL_COLOR, fontSize: CENTER_LABEL_SIZE }}
      >
        Max Pull
      </p>
      <p
        className="
          absolute top-[44px] left-1/2 -translate-x-1/2
          text-center whitespace-nowrap font-bold tracking-[-0.96px] leading-[42px]
        "
        style={{ color: VALUE_COLOR, fontSize: CENTER_VALUE_SIZE }}
      >
        {formatDollars(maxPull)}
      </p>

      {/* Left — "Min Value" + value */}
      <div className="absolute top-[34px] left-5 flex flex-col items-start gap-[2px]">
        <p
          className="font-medium tracking-[-0.1px] whitespace-nowrap leading-[20px]"
          style={{ color: LABEL_COLOR, fontSize: 'var(--co-StatsRow-labelSize, 13px)' }}
        >
          Min Value
        </p>
        <p
          className="font-bold tracking-[-0.56px] whitespace-nowrap leading-[28px]"
          style={{ color: VALUE_COLOR, fontSize: 'var(--co-StatsRow-valueSize, 20px)' }}
        >
          {formatDollars(minValue)}
        </p>
      </div>

      {/* Right — "Big Win Chance" + percent with accent glow. */}
      <div className="absolute top-[34px] right-5 flex flex-col items-end gap-[2px]">
        <p
          className="font-medium tracking-[-0.1px] whitespace-nowrap leading-[20px]"
          style={{ color: LABEL_COLOR, fontSize: 'var(--co-StatsRow-labelSize, 13px)' }}
        >
          Big Win Chance
        </p>
        <p
          className="font-bold tracking-[-0.56px] whitespace-nowrap leading-[28px]"
          style={{
            color: VALUE_COLOR,
            fontSize: 'var(--co-StatsRow-valueSize, 20px)',
            textShadow:
              '0 0 4px color-mix(in srgb, var(--ui-accent) 30%, transparent)',
          }}
        >
          {formatPercent(bigWinChance)}%
        </p>
      </div>
    </section>
  );
}
